/**
 * Headless drag-and-drop provider. Owns the whole drag lifecycle and the a11y
 * surface; renders nothing visual itself (the overlay is `DragOverlay`, the
 * highlights are the consumer's). Pointer and keyboard are two sensors driving
 * one state machine.
 *
 * Design notes worth keeping in your head while reading:
 *
 * - **Refs hold the live drag; React state holds only what must re-render.** The
 *   reducer (`status`/`active`/`over`) drives bucket highlights + announcements
 *   and changes only on bucket *crossing*. Pointer x/y and the rAF overlay
 *   follow never touch React — they write `transform` straight onto the overlay
 *   node, so a pointermove storm costs nothing.
 *
 * - **Drag handlers are recreated each render but stay correct**, because every
 *   mutable input is read through a ref (`sessionRef`, `callbacksRef`, the
 *   registries) and `dispatch`/`setState` are stable. The document listeners a
 *   drag installs therefore behave identically no matter which render's closure
 *   captured them — the same reason `useDragSelect` is safe.
 *
 * - **Droppable rects are read fresh on every move**, not cached. With a handful
 *   of buckets that's cheaper than a cache-plus-scroll-invalidation dance and is
 *   immune to stale-rect-after-scroll bugs by construction.
 */
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useReducer,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { pointerWithin } from './collision.ts'
import { nextDroppableInOrder } from './keyboard.ts'
import { dndReducer, initialDndState } from './reducer.ts'
import type {
  Announcements,
  CollisionDetection,
  DndData,
  DndStatus,
  DragActive,
  DragInputMode,
  DragOver,
  Orientation,
  Rect,
} from './types.ts'

/** Px the pointer must travel from press before a drag begins. Below it the
 *  press is a click — this is what keeps a tap on the handle from dragging. */
const ACTIVATION_DISTANCE = 5
/** Pointer bucket-crossing announcements are throttled this long so rapid drags
 *  don't machine-gun the live region. Keyboard moves announce immediately. */
const ANNOUNCE_DEBOUNCE_MS = 150
/** Distance (px) from the scroll container's edge at which auto-scroll kicks in. */
const AUTOSCROLL_EDGE = 56
/** Peak auto-scroll speed (px/frame) right at the edge; eases to 0 across the band. */
const AUTOSCROLL_MAX_SPEED = 16

/** Nearest scrollable ancestor of `node` (the one auto-scroll should drive). The
 *  app's scroll lives on an `overflow-y-auto` content div, not the window, so we
 *  resolve it from the dragged element rather than assuming `window`. */
function findScrollContainer(node: HTMLElement | null): HTMLElement | null {
  let el = node?.parentElement ?? null
  while (el) {
    const oy = getComputedStyle(el).overflowY
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return el
    el = el.parentElement
  }
  return null
}

interface DraggableEntry {
  id: string
  node: HTMLElement | null
  /**
   * The element that owns the drag listeners + focus (a grip handle, when the
   * consumer uses one). `node` is the *measured* element (sizes the overlay,
   * resolves the scroll container); the activator is where focus returns after a
   * keyboard drop. Falls back to `node` when the consumer drags by the body.
   */
  activatorNode: HTMLElement | null
  getData: () => DndData
  isDisabled: () => boolean
}

interface DroppableEntry {
  id: string
  node: HTMLElement | null
  getData: () => DndData
  isDisabled: () => boolean
  orientation: Orientation
}

/** The live drag session, read by document listeners (never React state). */
interface Session {
  mode: DragInputMode
  id: string
  data: DndData
  /** Pointer press origin; `null` for keyboard drags. */
  pointerOrigin: { x: number; y: number } | null
  /** Pointer drags arm on press and "begin" once past the threshold. */
  activationMet: boolean
  /** The dragged element's rect, snapshotted at drag start (overlay anchor). */
  sourceRect: Rect
  over: DragOver | null
}

interface DndContextValue {
  status: DndStatus
  activeId: string | null
  activeData: DndData | null
  activeRect: Rect | null
  overId: string | null
  mode: DragInputMode | null
  instructionsId: string
  registerDraggable: (entry: DraggableEntry) => () => void
  registerDroppable: (entry: DroppableEntry) => () => void
  setOverlayNode: (node: HTMLElement | null) => void
  startPointerDrag: (id: string, e: ReactPointerEvent) => void
  handleDraggableKeyDown: (id: string, e: ReactKeyboardEvent) => void
}

const DndCtx = createContext<DndContextValue | null>(null)

export function useDndContext(): DndContextValue {
  const ctx = useContext(DndCtx)
  if (ctx === null) {
    throw new Error('useDraggable / useDroppable must be used inside a <DndContext>')
  }
  return ctx
}

export interface DndContextProps {
  children: ReactNode
  /** Localized strings for the `aria-live` region — the consumer maps these onto
   *  its own i18n; the package ships no strings. */
  announcements: Announcements
  /** Localized keyboard help, rendered into a hidden element every handle points
   *  at via `aria-describedby`. */
  keyboardInstructions: string
  onDragStart?: (e: { active: DragActive }) => void
  onDragOver?: (e: { active: DragActive; over: DragOver | null }) => void
  onDragEnd?: (e: { active: DragActive; over: DragOver | null }) => void
  onDragCancel?: (e: { active: DragActive }) => void
  /** Pointer collision strategy. Defaults to `pointerWithin` (right for buckets). */
  collisionDetection?: CollisionDetection
  /** Drive the nearest scrollable ancestor when the pointer nears its edge (and
   *  `scrollIntoView` off-screen targets on keyboard moves). Defaults to `true`. */
  autoScroll?: boolean
}

function toRect(r: DOMRect): Rect {
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

function arrowDir(key: string): 1 | -1 | 0 {
  if (key === 'ArrowDown' || key === 'ArrowRight') return 1
  if (key === 'ArrowUp' || key === 'ArrowLeft') return -1
  return 0
}

export function DndContext({
  children,
  announcements,
  keyboardInstructions,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDragCancel,
  collisionDetection,
  autoScroll = true,
}: DndContextProps) {
  const [state, dispatch] = useReducer(dndReducer, initialDndState)
  const [activeRect, setActiveRect] = useState<Rect | null>(null)
  const [announcement, setAnnouncement] = useState('')
  const instructionsId = useId()

  // Registries. Keyed by id; entries carry live getters so data/disabled changes
  // don't force re-registration.
  const draggablesRef = useRef<Map<string, DraggableEntry>>(new Map())
  const droppablesRef = useRef<Map<string, DroppableEntry>>(new Map())

  const sessionRef = useRef<Session | null>(null)
  const pointerRef = useRef<{ x: number; y: number } | null>(null)
  const rafRef = useRef<number | null>(null)
  const overlayNodeRef = useRef<HTMLElement | null>(null)
  const pointerCleanupRef = useRef<(() => void) | null>(null)
  const announceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevUserSelectRef = useRef('')
  // A document-wide `cursor: grabbing` rule, live only during a pointer drag.
  const dragCursorStyleRef = useRef<HTMLStyleElement | null>(null)
  // Auto-scroll: the container to drive, the current velocity, and its rAF loop.
  const scrollContainerRef = useRef<HTMLElement | null>(null)
  const autoScrollVelRef = useRef(0)
  const autoScrollRafRef = useRef<number | null>(null)

  // Latest props, read by the (closure-captured) document listeners.
  const callbacksRef = useRef({
    announcements,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDragCancel,
    collision: collisionDetection ?? pointerWithin,
    autoScroll,
  })
  callbacksRef.current = {
    announcements,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDragCancel,
    collision: collisionDetection ?? pointerWithin,
    autoScroll,
  }

  // --- announcements -------------------------------------------------------
  function announceNow(text: string) {
    if (announceTimerRef.current) {
      clearTimeout(announceTimerRef.current)
      announceTimerRef.current = null
    }
    setAnnouncement(text)
  }
  function announceDebounced(text: string) {
    if (announceTimerRef.current) clearTimeout(announceTimerRef.current)
    announceTimerRef.current = setTimeout(() => setAnnouncement(text), ANNOUNCE_DEBOUNCE_MS)
  }

  // --- body lock -----------------------------------------------------------
  function lockBody(withGrabCursor: boolean) {
    prevUserSelectRef.current = document.body.style.userSelect
    document.body.style.userSelect = 'none'
    // For a pointer drag, hold the grabbing cursor for the whole gesture. A
    // plain `body { cursor }` loses to whatever element sits under the pointer
    // (other grips are `cursor-grab`, pills `cursor-pointer`, …), so force it
    // globally with `!important` and drop the rule on release. Keyboard drags
    // have no pointer, so they skip this.
    if (withGrabCursor) {
      const style = document.createElement('style')
      style.textContent = '*{cursor:grabbing !important}'
      document.head.appendChild(style)
      dragCursorStyleRef.current = style
    }
  }
  function unlockBody() {
    document.body.style.userSelect = prevUserSelectRef.current
    dragCursorStyleRef.current?.remove()
    dragCursorStyleRef.current = null
  }

  // --- registry reads ------------------------------------------------------
  function droppableGeometries() {
    const out: { id: string; rect: Rect; disabled: boolean }[] = []
    for (const entry of droppablesRef.current.values()) {
      if (!entry.node) continue
      out.push({
        id: entry.id,
        rect: toRect(entry.node.getBoundingClientRect()),
        disabled: entry.isDisabled(),
      })
    }
    return out
  }
  function orderedDroppableIds(): string[] {
    const entries = [...droppablesRef.current.values()].filter((e) => e.node)
    entries.sort((a, b) => {
      const pos = a.node!.compareDocumentPosition(b.node!)
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1
      return 0
    })
    return entries.map((e) => e.id)
  }
  function disabledDroppableIds(): Set<string> {
    const s = new Set<string>()
    for (const entry of droppablesRef.current.values()) {
      if (entry.isDisabled()) s.add(entry.id)
    }
    return s
  }
  function droppableData(id: string): DndData {
    return droppablesRef.current.get(id)?.getData() ?? {}
  }
  /** The droppable whose node contains the draggable — the keyboard pickup's
   *  starting bucket. */
  function sourceDroppableId(draggableId: string): string | null {
    const dnode = draggablesRef.current.get(draggableId)?.node
    if (!dnode) return null
    for (const entry of droppablesRef.current.values()) {
      if (entry.node && entry.node.contains(dnode)) return entry.id
    }
    return null
  }

  // --- overlay positioning (imperative, bypasses React) --------------------
  function scheduleOverlayFollow() {
    if (rafRef.current !== null) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      const node = overlayNodeRef.current
      const s = sessionRef.current
      const p = pointerRef.current
      if (!node || !s || !p || !s.pointerOrigin) return
      const dx = p.x - s.pointerOrigin.x
      const dy = p.y - s.pointerOrigin.y
      node.style.transform = `translate3d(${dx}px, ${dy}px, 0)`
    })
  }
  function snapOverlayToDroppable(overId: string) {
    const node = overlayNodeRef.current
    const s = sessionRef.current
    if (!node || !s) return
    const target = droppablesRef.current.get(overId)?.node
    if (!target) return
    const tr = target.getBoundingClientRect()
    node.style.transform = `translate3d(${tr.left - s.sourceRect.left}px, ${tr.top - s.sourceRect.top}px, 0)`
  }

  // --- auto-scroll (pointer; the window doesn't scroll itself for a custom
  //     pointer drag, so we drive the nearest scrollable ancestor) -----------
  function autoScrollSpeed(distFromEdge: number): number {
    const ratio = 1 - Math.max(0, Math.min(1, distFromEdge / AUTOSCROLL_EDGE))
    return Math.ceil(AUTOSCROLL_MAX_SPEED * ratio)
  }
  function autoScrollTick() {
    autoScrollRafRef.current = null
    const s = sessionRef.current
    const c = scrollContainerRef.current
    const v = autoScrollVelRef.current
    if (!s || !c || v === 0) return
    const before = c.scrollTop
    c.scrollTop = before + v
    if (c.scrollTop === before) return // hit the top/bottom — nothing more to do
    // The pointer hasn't moved, but the page under it has — re-resolve the target.
    const p = pointerRef.current
    if (p) {
      updateOver(
        callbacksRef.current.collision({ pointer: p, droppables: droppableGeometries() }),
        false,
      )
    }
    autoScrollRafRef.current = requestAnimationFrame(autoScrollTick)
  }
  function updateAutoScroll(clientY: number) {
    if (!callbacksRef.current.autoScroll) return
    const c = scrollContainerRef.current
    if (!c) {
      autoScrollVelRef.current = 0
      return
    }
    const rect = c.getBoundingClientRect()
    const fromTop = clientY - rect.top
    const fromBottom = rect.bottom - clientY
    let v = 0
    if (fromTop < AUTOSCROLL_EDGE) v = -autoScrollSpeed(fromTop)
    else if (fromBottom < AUTOSCROLL_EDGE) v = autoScrollSpeed(fromBottom)
    autoScrollVelRef.current = v
    if (v !== 0 && autoScrollRafRef.current === null) {
      autoScrollRafRef.current = requestAnimationFrame(autoScrollTick)
    }
  }
  function stopAutoScroll() {
    autoScrollVelRef.current = 0
    if (autoScrollRafRef.current !== null) {
      cancelAnimationFrame(autoScrollRafRef.current)
      autoScrollRafRef.current = null
    }
    scrollContainerRef.current = null
  }

  // --- focus restoration ---------------------------------------------------
  // Only for keyboard drags — moving focus after a *mouse* drop would flash a
  // focus ring the mouse user didn't ask for.
  function restoreFocusAfterDrop(id: string, droppedIntoNewLocation: boolean) {
    // Focus the activator (the grip handle) so the focus ring lands on the
    // grabbable element, not the larger measured card; falls back to `node`.
    const focusEntry = (entry: DraggableEntry | undefined) =>
      (entry?.activatorNode ?? entry?.node)?.focus()
    if (droppedIntoNewLocation) {
      // The card re-mounts in its new bucket after the consumer's state update;
      // grab the freshly-registered node on the next frame.
      requestAnimationFrame(() => focusEntry(draggablesRef.current.get(id)))
    } else {
      focusEntry(draggablesRef.current.get(id))
    }
  }

  // --- shared transitions --------------------------------------------------
  function updateOver(overId: string | null, immediate: boolean) {
    const s = sessionRef.current
    if (!s) return
    const prevId = s.over?.id ?? null
    if (prevId === overId) return
    const over: DragOver | null = overId ? { id: overId, data: droppableData(overId) } : null
    s.over = over
    dispatch({ type: 'DRAG_OVER', over })
    const active: DragActive = { id: s.id, data: s.data }
    callbacksRef.current.onDragOver?.({ active, over })
    const text = callbacksRef.current.announcements.onDragOver({ active, over })
    if (immediate) announceNow(text)
    else announceDebounced(text)
  }

  function finishDrag(commit: boolean) {
    const s = sessionRef.current
    if (!s) return
    pointerCleanupRef.current?.()
    pointerCleanupRef.current = null
    if (announceTimerRef.current) {
      clearTimeout(announceTimerRef.current)
      announceTimerRef.current = null
    }
    const active: DragActive = { id: s.id, data: s.data }
    const droppedInto = commit && s.over !== null
    const wasKeyboard = s.mode === 'keyboard'
    // A drop back onto the source bucket is a no-op move (no new location).
    const movedLocation = droppedInto && s.over!.id !== sourceDroppableId(s.id)
    if (droppedInto) {
      callbacksRef.current.onDragEnd?.({ active, over: s.over })
      announceNow(callbacksRef.current.announcements.onDragEnd({ active, over: s.over }))
    } else {
      callbacksRef.current.onDragCancel?.({ active })
      announceNow(callbacksRef.current.announcements.onDragCancel({ active }))
    }
    sessionRef.current = null
    pointerRef.current = null
    stopAutoScroll()
    unlockBody()
    setActiveRect(null)
    dispatch({ type: droppedInto ? 'DRAG_END' : 'DRAG_CANCEL' })
    if (wasKeyboard) restoreFocusAfterDrop(active.id, movedLocation)
  }

  // --- pointer sensor ------------------------------------------------------
  function startPointerDrag(id: string, e: ReactPointerEvent) {
    if (e.button !== 0) return // primary button only
    if (sessionRef.current) return // a drag is already live
    const entry = draggablesRef.current.get(id)
    if (!entry || entry.isDisabled() || !entry.node) return

    sessionRef.current = {
      mode: 'pointer',
      id,
      data: entry.getData(),
      pointerOrigin: { x: e.clientX, y: e.clientY },
      activationMet: false,
      sourceRect: toRect(entry.node.getBoundingClientRect()),
      over: null,
    }

    const onMove = (ev: PointerEvent) => {
      const s = sessionRef.current
      if (!s || s.mode !== 'pointer' || !s.pointerOrigin) return
      const dx = ev.clientX - s.pointerOrigin.x
      const dy = ev.clientY - s.pointerOrigin.y
      if (!s.activationMet) {
        if (Math.hypot(dx, dy) < ACTIVATION_DISTANCE) return
        s.activationMet = true
        beginPointerDrag(s, ev.clientX, ev.clientY)
      }
      pointerRef.current = { x: ev.clientX, y: ev.clientY }
      scheduleOverlayFollow()
      updateAutoScroll(ev.clientY)
      const overId = callbacksRef.current.collision({
        pointer: { x: ev.clientX, y: ev.clientY },
        droppables: droppableGeometries(),
      })
      updateOver(overId, false)
    }
    const onUp = () => {
      const s = sessionRef.current
      if (s && !s.activationMet) {
        // Never crossed the threshold → it was a click, not a drag.
        cleanup()
        sessionRef.current = null
        return
      }
      finishDrag(true)
    }
    const onCancel = () => finishDrag(false)
    const cleanup = () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      document.removeEventListener('pointercancel', onCancel)
    }

    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
    document.addEventListener('pointercancel', onCancel)
    pointerCleanupRef.current = cleanup
  }

  function beginPointerDrag(s: Session, x: number, y: number) {
    const active: DragActive = { id: s.id, data: s.data }
    const overId = callbacksRef.current.collision({
      pointer: { x, y },
      droppables: droppableGeometries(),
    })
    const over: DragOver | null = overId ? { id: overId, data: droppableData(overId) } : null
    s.over = over
    scrollContainerRef.current = findScrollContainer(draggablesRef.current.get(s.id)?.node ?? null)
    lockBody(true)
    setActiveRect(s.sourceRect)
    dispatch({ type: 'DRAG_START', mode: 'pointer', active, over })
    callbacksRef.current.onDragStart?.({ active })
    announceNow(callbacksRef.current.announcements.onDragStart({ active }))
  }

  // --- keyboard sensor -----------------------------------------------------
  function handleDraggableKeyDown(id: string, e: ReactKeyboardEvent) {
    const s = sessionRef.current
    const draggingThis = !!s && s.mode === 'keyboard' && s.id === id

    if (e.key === ' ' || e.key === 'Enter') {
      if (draggingThis) {
        e.preventDefault()
        finishDrag(true)
        return
      }
      if (s) return // some other drag owns the session
      const entry = draggablesRef.current.get(id)
      if (!entry || entry.isDisabled() || !entry.node) return
      e.preventDefault()
      startKeyboardDrag(id, entry)
      return
    }

    if (!draggingThis) return

    if (e.key === 'Escape') {
      e.preventDefault()
      finishDrag(false)
      return
    }
    if (e.key === 'Tab') {
      // Trap focus for the duration of the grab.
      e.preventDefault()
      return
    }
    const dir = arrowDir(e.key)
    if (dir === 0) return
    e.preventDefault()
    const ordered = orderedDroppableIds()
    const current = s!.over?.id ?? sourceDroppableId(id) ?? ordered[0]
    if (current === undefined) return
    const nextId = nextDroppableInOrder(current, dir, ordered, disabledDroppableIds())
    updateOver(nextId, true)
    // Bring an off-screen target into view first, then snap the overlay onto its
    // post-scroll position.
    if (callbacksRef.current.autoScroll) {
      droppablesRef.current.get(nextId)?.node?.scrollIntoView({ block: 'nearest' })
    }
    snapOverlayToDroppable(nextId)
  }

  function startKeyboardDrag(id: string, entry: DraggableEntry) {
    const rect = toRect(entry.node!.getBoundingClientRect())
    const srcId = sourceDroppableId(id)
    const over: DragOver | null = srcId ? { id: srcId, data: droppableData(srcId) } : null
    sessionRef.current = {
      mode: 'keyboard',
      id,
      data: entry.getData(),
      pointerOrigin: null,
      activationMet: true,
      sourceRect: rect,
      over,
    }
    const active: DragActive = { id, data: entry.getData() }
    lockBody(false)
    setActiveRect(rect)
    dispatch({ type: 'DRAG_START', mode: 'keyboard', active, over })
    callbacksRef.current.onDragStart?.({ active })
    announceNow(callbacksRef.current.announcements.onDragStart({ active }))
  }

  // --- registries (stable; hooks register in an effect) --------------------
  const registerDraggable = useRef((entry: DraggableEntry) => {
    draggablesRef.current.set(entry.id, entry)
    return () => {
      if (draggablesRef.current.get(entry.id) === entry) draggablesRef.current.delete(entry.id)
    }
  }).current
  const registerDroppable = useRef((entry: DroppableEntry) => {
    droppablesRef.current.set(entry.id, entry)
    return () => {
      if (droppablesRef.current.get(entry.id) === entry) droppablesRef.current.delete(entry.id)
    }
  }).current
  const setOverlayNode = useRef((node: HTMLElement | null) => {
    overlayNodeRef.current = node
  }).current

  // Cancel an in-flight drag if the window/tab loses focus (alt-tab, OS gesture,
  // incoming call) — a drag that survives a context switch is a footgun.
  useEffect(() => {
    if (state.status !== 'dragging') return
    const cancel = () => finishDrag(false)
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') finishDrag(false)
    }
    window.addEventListener('blur', cancel)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('blur', cancel)
      document.removeEventListener('visibilitychange', onVisibility)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status])

  // Unmount mid-drag: tear down listeners, raf, and the body lock.
  useEffect(
    () => () => {
      pointerCleanupRef.current?.()
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      if (autoScrollRafRef.current !== null) cancelAnimationFrame(autoScrollRafRef.current)
      if (announceTimerRef.current) clearTimeout(announceTimerRef.current)
      document.body.style.userSelect = prevUserSelectRef.current
      dragCursorStyleRef.current?.remove()
      dragCursorStyleRef.current = null
    },
    [],
  )

  const value: DndContextValue = {
    status: state.status,
    activeId: state.active?.id ?? null,
    activeData: state.active?.data ?? null,
    activeRect,
    overId: state.over?.id ?? null,
    mode: state.mode,
    instructionsId,
    registerDraggable,
    registerDroppable,
    setOverlayNode,
    startPointerDrag,
    handleDraggableKeyDown,
  }

  return (
    <DndCtx.Provider value={value}>
      {children}
      <span id={instructionsId} className="sr-only">
        {keyboardInstructions}
      </span>
      <div aria-live="assertive" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
    </DndCtx.Provider>
  )
}
